import { ChartType } from 'components/Charts/utils'
import { ActiveQuery } from 'components/Tokens/TokenDetails/ChartSection'
import ms from 'ms'
import { TimePeriod } from './util'

export enum DataQuality {
  VALID,
  INVALID,
  STALE,
}

/** Used for expecting the same data freshness regardless of time period, e.g. 1y price chart should still have a recent point */
const CONSTANT_STALENESS: Record<TimePeriod, number> = {
  [TimePeriod.HOUR]: ms('15m'),
  [TimePeriod.DAY]: ms('15m'),
  [TimePeriod.WEEK]: ms('15m'),
  [TimePeriod.MONTH]: ms('15m'),
  [TimePeriod.YEAR]: ms('15m'),
}

/** Used decreasing freshness regardless of time period, e.g. 1h volume chart has more recent data than 1y volume chart */
const GRANULAR_STALENESS: Record<TimePeriod, number> = {
  [TimePeriod.HOUR]: ms('15m'),
  [TimePeriod.DAY]: ms('4h'),
  [TimePeriod.WEEK]: ms('1d'),
  [TimePeriod.MONTH]: ms('4d'),
  [TimePeriod.YEAR]: ms('30d'),
}

/** Maps ChartType and TimePeriod to expected data freshness threshold */
const TIME_PERIOD_STALE_THRESHOLD_MAP: Record<ChartType, Record<TimePeriod, number> | undefined> = {
  [ChartType.PRICE]: CONSTANT_STALENESS,
  [ChartType.VOLUME]: GRANULAR_STALENESS,
  // TODO(WEB-3725): Update TVL data to be constant staleness by appending latest TVL data to end of array
  [ChartType.TVL]: GRANULAR_STALENESS,
  // Liquidity chart does not have a time axis
  [ChartType.LIQUIDITY]: undefined,
}

export function getTimestampedDataQuality(
  timestamp: number | undefined,
  chartType: ChartType,
  timePeriod: TimePeriod
): DataQuality {
  if (!timestamp) return DataQuality.INVALID

  const stalenessThreshold = TIME_PERIOD_STALE_THRESHOLD_MAP[chartType]?.[timePeriod] ?? -Infinity
  return Date.now() - timestamp * 1000 > stalenessThreshold ? DataQuality.STALE : DataQuality.VALID
}

export function getChartDataQuality(activeQuery: ActiveQuery, timePeriod: TimePeriod): DataQuality {
  let data: readonly { timestamp: number; value: number }[] | undefined
  switch (activeQuery.chartType) {
    case ChartType.PRICE:
      data = activeQuery.data?.token?.market?.priceHistory
      break
    case ChartType.VOLUME:
      data = activeQuery.data?.token?.market?.historicalVolume
      break
    case ChartType.TVL:
      data = activeQuery.data?.token?.market?.historicalTvl
      break
  }

  if (!data?.length || data.length < 3) {
    return DataQuality.INVALID
  }

  return getTimestampedDataQuality(data[data.length - 1].timestamp, activeQuery.chartType, timePeriod)
}
